import React, { useState, useEffect } from 'react';
import { MdGroup, MdStorage, MdLock, MdExplicit, MdAssessment, MdOpenInNew } from "react-icons/md";
import { useTheme } from '../../contexts/ThemeContext';

import { Page } from 'components';
import { Button, Col, Row, Spinner, Container, Badge } from 'react-bootstrap'
import { HorizontalBar } from 'react-chartjs-2';

import CertificadoDash from './components/CertificadoDash';
import Reload from '../../components/Reload/Reload';
import Header from 'components/Header/Header';
import ApiMServer from 'services/ApiMServer';
import ApiGServer from 'services/ApiGServer';
import ApiGSenha from 'services/ApiGSenha';
import Api from 'services/Api';
import {
  BRAND,
  KPI,
  chartColors,
  getChartTextColor,
  getChartGridColor,
  getDashboardCardSurface,
  getDashboardShellStyles,
  getKpiAccentByIndex
} from 'constants/dashboardColors';
import './Home.css';

const getChartOptions = (isDarkTheme) => {
  const chartTextColor = getChartTextColor(isDarkTheme);
  const chartGridColor = getChartGridColor(isDarkTheme);

  return {
    scales: {
      y: [{
        ticks: {
          beginAtZero: true,
          fontColor: chartTextColor,
          fontStyle: 'bold',
          precision: 0
        },
        gridLines: {
          display: false
        },
        maxBarThickness: 30
      }],
      x: [{
        ticks: {
          beginAtZero: true,
          fontColor: chartTextColor,
          padding: 10,
          maxRotation: 0
        },
        gridLines: {
          color: chartGridColor,
          zeroLineColor: isDarkTheme ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
        }
      }]
    },
    legend: {
      display: false
    },
    maintainAspectRatio: false,
    responsive: true,
    tooltips: {
      backgroundColor: isDarkTheme ? 'rgba(30, 30, 30, 0.9)' : 'rgba(255, 255, 255, 0.9)',
      titleFontColor: isDarkTheme ? '#f8f9fa' : '#333',
      bodyFontColor: isDarkTheme ? '#adb5bd' : '#666',
      borderColor: isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
      borderWidth: 1,
      displayColors: true,
      cornerRadius: 6,
      caretSize: 6,
      titleFontStyle: 'bold',
      callbacks: {
        title: (items) => `Versão: ${items[0].label}`,
        label: (item) => `Quantidade: ${item.raw || item.value}`
      }
    }
  };
};

const HomeStatCard = ({ icon, label, value, accent }) => (
  <Col xs={12} sm={6} md={6} lg={3}>
    <div
      className="h-100 home-stat-card"
      style={{
        borderTop: `4px solid ${accent}`,
      }}
    >
      <div className="d-flex justify-content-between align-items-start">
        <div className="home-stat-card__body">
          <p className="home-stat-card__label mb-1">{label}</p>
          <h3 className="home-stat-card__value mb-0 fw-bold">{value}</h3>
        </div>
        <div
          className="home-stat-card__icon d-inline-flex align-items-center justify-content-center flex-shrink-0"
          style={{ background: `${accent}30`, color: accent }}
        >
          {icon}
        </div>
      </div>
    </div>
  </Col>
);

const HomeChartPanel = ({ title, badge, accent, children, action }) => (
  <div
    className="mb-4 p-3 home-chart-panel"
    style={{
      ...getDashboardCardSurface(),
      borderTop: `4px solid ${accent}`
    }}
  >
    <div className="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h5 className="fw-bold mb-0" style={{ color: BRAND.darkGreen }}>{title}</h5>
        {badge && <Badge className="mt-1 badge-light" pill>{badge}</Badge>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

const createBarGradient = (ctx, colors, isDark) => {
  const gradient = ctx.createLinearGradient(0, 0, 300, 0);
  gradient.addColorStop(0, isDark ? colors.dark : colors.light);
  gradient.addColorStop(1, isDark ? `${colors.dark}99` : `${colors.light}99`);
  return gradient;
};

// Mapeamento entre as permissões e as URLs corretas
const PAGE_TO_URL_MAP = {
  'users': '/users',
  'cooperatives': '/cooperatives',
  'units': '/units',
  'equipments': '/equipments',
  'nir': '/nir',
  'equipmentsreplace': '/equipments-replace',
  'updates': '/updates',
  'models3d': '/models-3d',
  'logs': '/logs',
  'maps': '/maps',
  'mapsunits': '/maps?tab=units',
  'support': '/support',
  'supportnir': '/supportnir',
  'supportdry': '/supportdry',
  'supportOrange': '/remote-access',
  'supportiot': '/supportiot',
  'equipmentsQL': '/equipmentsQL',
  'notifications': '/notifications',
  'userspassword': '/userspassword',
  'general': '/settings/general',
};

const Home = () => {
  const user = JSON.parse(localStorage.getItem('userData'));
  const [pagePermission, setPagePermission] = useState(0);
  const [n_users, setNusers] = useState();
  const [n_measurements, setNMeasurements] = useState();
  const [n_unblocks, setNUnblocks] = useState();
  const [loading, setLoading] = useState(true);
  const { darkMode } = useTheme();
  const dashboardStyles = getDashboardShellStyles(darkMode);
  const [options, setOptions] = useState(getChartOptions(darkMode));

  const [equipmentMetrics, setEquipmentMetrics] = useState({
    simt: { n: 0, softVesions: {}, labels: [], data: [] },
    bimt: { n: 0, softVesions: {}, labels: [], data: [] },
    cpmt: { n: 0, softVesions: {}, labels: [], data: [] },
    eemt: { n: 0, softVesions: {}, labels: [], data: [] },
    esmr: { n: 0, softVesions: {}, labels: [], data: [] },
  });

  // Observa mudanças no tema para atualizar as opções do gráfico
  useEffect(() => {
    setOptions(getChartOptions(darkMode));
  }, [darkMode]);

  useEffect(() => {
    if (user?.permission?.home?.read !== true) {
      let keys = Object.keys(user?.permission);
      let hasAnyPermission = false;

      for (let i = 0; i < keys.length; i++) {
        let page = keys[i];
        if (user?.permission[page]?.read) {
          hasAnyPermission = true;
          const redirectUrl = PAGE_TO_URL_MAP[page] || '/' + page;
          window.location.replace(redirectUrl);
          return;
        }
      }

      // Só aplica "Acesso Restrito" se não tiver permissão para nenhuma página
      if (!hasAnyPermission) {
        setPagePermission(1);
      }
    } else {
      setPagePermission(2);
    }
  }, [user]);

  useEffect(() => {
    const handleGetUser = async () => {
      const storedUser = localStorage.getItem('userData');
      if (!storedUser) return;

      try {
        const { _id } = JSON.parse(storedUser);
        const { data: newUserData } = await ApiMServer.get(`/users/${_id}`);

        if (newUserData) {
          const updatedUser = {
            ...JSON.parse(storedUser),
            ...newUserData,
            theme: 'dark',
          };
          localStorage.setItem('userData', JSON.stringify(updatedUser));
        }
      } catch (error) {
        console.error('Erro ao buscar dados do usuário:', error);
      }
    };
    handleGetUser()

    // Funções separadas para cada request
    const fetchUnlockCount = async () => {
      try {
        let unlockcount = await ApiGSenha.get('/log/unlockcount');
        setNUnblocks(unlockcount?.data?.count);
      } catch (error) {
        setNUnblocks(undefined);
        console.log("Erro ao buscar unlockcount:", error.message);
      }
    };

    const fetchDashboard = async () => {
      try {
        let dashboard = await ApiMServer.get('/dashboard');
        setNusers(dashboard?.data?.n_users);
      } catch (error) {
        setNusers(undefined);
        console.log("Erro ao buscar dashboard:", error.message);
      }
    };

    const fetchMeasurements = async () => {
      try {
        let measurements = await Api.get('/measurements/count');
        setNMeasurements(measurements?.data?.n_measurements);
      } catch (error) {
        setNMeasurements(undefined);
        console.log("Erro ao buscar measurements:", error.message);
      }
    };

    const fetchSoftwares = async () => {
      try {
        let softwares = await ApiGServer.get('/equipment/metrics');
        const data = softwares.data;

        // Função para comparar versões (ex: "2.1.0" > "2.0.9")
        const compareVersions = (v1, v2) => {
          const parts1 = v1.split('.').map(Number);
          const parts2 = v2.split('.').map(Number);

          for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
            const num1 = parts1[i] || 0;
            const num2 = parts2[i] || 0;

            if (num1 > num2) return -1; // v1 é mais recente
            if (num1 < num2) return 1;  // v2 é mais recente
          }
          return 0; // são iguais
        };

        const sortVersionsDescending = (versions) => {
          return Object.entries(versions)
            .sort((a, b) => compareVersions(a[0], b[0]))
            .reduce((acc, [key, value]) => {
              acc[key] = value;
              return acc;
            }, {});
        };

        const sortedSimt = sortVersionsDescending(data.simt.softVesions);
        data.simt.labels = Object.keys(sortedSimt);
        data.simt.data = Object.values(sortedSimt);

        const sortedBimt = sortVersionsDescending(data.bimt.softVesions);
        data.bimt.labels = Object.keys(sortedBimt);
        data.bimt.data = Object.values(sortedBimt);

        const sortedCpmt = sortVersionsDescending(data.cpmt.softVesions);
        data.cpmt.labels = Object.keys(sortedCpmt);
        data.cpmt.data = Object.values(sortedCpmt);

        const sortedEemt = sortVersionsDescending(data.eemt.softVesions);
        data.eemt.labels = Object.keys(sortedEemt);
        data.eemt.data = Object.values(sortedEemt);

        const sortedEsmr = sortVersionsDescending(data.esmr.softVesions);
        data.esmr.labels = Object.keys(sortedEsmr);
        data.esmr.data = Object.values(sortedEsmr);

        setEquipmentMetrics(data);
      } catch (error) {
        setEquipmentMetrics({
          simt: { n: 0, softVesions: {}, labels: [], data: [] },
          bimt: { n: 0, softVesions: {}, labels: [], data: [] },
          cpmt: { n: 0, softVesions: {}, labels: [], data: [] },
          eemt: { n: 0, softVesions: {}, labels: [], data: [] },
          esmr: { n: 0, softVesions: {}, labels: [], data: [] },
        });
        console.log("Erro ao buscar softwares:", error.message);
      }
    };

    const getAll = async () => {
      // Executa todas as funções em paralelo, mas cada uma trata seu próprio erro
      await Promise.allSettled([
        fetchUnlockCount(),
        fetchDashboard(),
        fetchMeasurements(),
        fetchSoftwares()
      ]);

      setTimeout(() => {
        setLoading(false);
      }, 500);
    };
    if (user?.permission?.home?.read === true) {
      getAll();
    }
  }, []);

  const handlePlanilha = async (serial) => {
    try {
      window.open(`${process.env.REACT_APP_API_MOTOMCO}/dashboard/activelogsfilter?typeSerial=${serial}`, "_blank")
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <>
      {pagePermission === 0 ? (
        <div className="d-flex align-items-center justify-content-center" style={{ height: '100vh' }}>
          <Spinner animation="border" style={{ color: 'var(--brand-light-green)' }} size="lg" />
          <span className="ms-2">Carregando...</span>
        </div>
      ) : pagePermission === 1 ? (
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: '100vh' }}>
          <div className="text-center p-4 dashboard-card" style={{ maxWidth: '500px', borderRadius: '16px' }}>
            <MdLock size={48} style={{ color: 'var(--brand-light-green)', marginBottom: '20px' }} />
            <h2>Acesso Restrito</h2>
            <p className="text-muted">Você não tem permissão para acessar o sistema. Entre em contato com o administrador.</p>
          </div>
        </div>
      ) : pagePermission === 2 ? (

        <Page className="p-4" title="DASHBOARD">
          <Header page={"DASHBOARD"} />

          {loading && <Reload />}

          <Container fluid className="px-0">
            <div className="home-dashboard" style={dashboardStyles.shell}>
            <div className="mb-4 home-overview-panel" style={{ ...dashboardStyles.panel, padding: '20px' }}>
                <h4 className="fw-bold mb-4 section-header" style={dashboardStyles.titleText}>
                  Visão Geral
                </h4>
                <Row className="g-3">
                  <HomeStatCard
                    icon={<MdGroup size={28} />}
                    label="Usuários"
                    value={n_users ? n_users : <Spinner animation="border" style={{ color: KPI.secondary }} size="sm" />}
                    accent={getKpiAccentByIndex(0)}
                  />
                  <HomeStatCard
                    icon={<MdExplicit size={28} />}
                    label="Equipamentos"
                    value={
                      equipmentMetrics?.count !== undefined
                        ? equipmentMetrics.count
                        : <Spinner animation="border" style={{ color: KPI.secondary }} size="sm" />
                    }
                    accent={getKpiAccentByIndex(1)}
                  />
                  <HomeStatCard
                    icon={<MdStorage size={28} />}
                    label="Medidas Realizadas"
                    value={
                      n_measurements
                        ? n_measurements
                        : <Spinner animation="border" style={{ color: KPI.secondary }} size="sm" />
                    }
                    accent={getKpiAccentByIndex(2)}
                  />
                  <HomeStatCard
                    icon={<MdLock size={28} />}
                    label="Ativações de Equipamentos"
                    value={
                      n_unblocks
                        ? n_unblocks
                        : <Spinner animation="border" style={{ color: KPI.secondary }} size="sm" />
                    }
                    accent={getKpiAccentByIndex(3)}
                  />
                </Row>
            </div>

            <div className="mb-4" style={{ ...dashboardStyles.panel, padding: '20px' }}>
                <h4 className="mb-4 fw-bold section-header" style={dashboardStyles.titleText}>Distribuição de Software por Equipamento</h4>

                <Row className="g-2 mb-3">
                  <Col xs={12} lg={6}>
                    <HomeChartPanel
                      title="SIMT"
                      badge={`${equipmentMetrics.simt.n} equipamentos`}
                      accent={chartColors.simt.light}
                      action={
                        <Button
                          className="px-3 btn-theme-outline"
                          onClick={() => { handlePlanilha("SIMT") }}
                          disabled={user?.permission?.home?.write !== true}
                          variant="outline-success"
                          size="sm"
                        >
                          <MdAssessment className="me-1" /> Relatório
                        </Button>
                      }
                    >
                      <div style={{ height: '350px', position: 'relative' }}>
                        <HorizontalBar
                          data={(canvas) => {
                            const ctx = canvas.getContext('2d');
                            const isDark = darkMode;

                            return {
                              labels: equipmentMetrics.simt.labels,
                              datasets: [
                                {
                                  label: 'Equipamentos',
                                  data: equipmentMetrics.simt.data,
                                  backgroundColor: createBarGradient(ctx, chartColors.simt, isDark),
                                  borderColor: isDark ? chartColors.simt.dark : chartColors.simt.light,
                                  borderWidth: 1,
                                  hoverBackgroundColor: isDark ? chartColors.simt.dark : chartColors.simt.light,
                                  hoverBorderColor: isDark ? '#ffffff' : '#000000',
                                  barPercentage: 0.7,
                                  categoryPercentage: 0.8
                                }
                              ]
                            };
                          }}
                          options={options}
                        />
                      </div>
                    </HomeChartPanel>

                    <HomeChartPanel
                      title="CPMT"
                      badge={`${equipmentMetrics.cpmt.n} equipamentos`}
                      accent={chartColors.cpmt.light}
                      action={
                        <Button
                          className="px-3 btn-theme-outline"
                          onClick={() => { handlePlanilha("CPMT") }}
                          disabled={user?.permission?.home?.write !== true}
                          variant="outline-success"
                          size="sm"
                        >
                          <MdAssessment className="me-1" /> Relatório
                        </Button>
                      }
                    >
                      <div style={{ height: '350px', position: 'relative' }}>
                        <HorizontalBar
                          data={(canvas) => {
                            const ctx = canvas.getContext('2d');
                            const isDark = darkMode;

                            return {
                              labels: equipmentMetrics.cpmt.labels,
                              datasets: [
                                {
                                  label: 'Equipamentos',
                                  data: equipmentMetrics.cpmt.data,
                                  backgroundColor: createBarGradient(ctx, chartColors.cpmt, isDark),
                                  borderColor: isDark ? chartColors.cpmt.dark : chartColors.cpmt.light,
                                  borderWidth: 1,
                                  hoverBackgroundColor: isDark ? chartColors.cpmt.dark : chartColors.cpmt.light,
                                  hoverBorderColor: isDark ? '#ffffff' : '#000000',
                                  barPercentage: 0.7,
                                  categoryPercentage: 0.8
                                }
                              ]
                            };
                          }}
                          options={options}
                        />
                      </div>
                    </HomeChartPanel>
                  </Col>

                  <Col xs={12} lg={6}>
                    <HomeChartPanel
                      title="BIMT"
                      badge={`${equipmentMetrics.bimt.n} equipamentos`}
                      accent={chartColors.bimt.light}
                      action={
                        <Button
                          className="px-3 btn-theme-outline"
                          onClick={() => { handlePlanilha("BIMT") }}
                          disabled={user?.permission?.home?.write !== true}
                          variant="outline-success"
                          size="sm"
                        >
                          <MdAssessment className="me-1" /> Relatório
                        </Button>
                      }
                    >
                      <div style={{ height: '350px', position: 'relative' }}>
                        <HorizontalBar
                          data={(canvas) => {
                            const ctx = canvas.getContext('2d');
                            const isDark = darkMode;

                            return {
                              labels: equipmentMetrics.bimt.labels,
                              datasets: [
                                {
                                  label: 'Equipamentos',
                                  data: equipmentMetrics.bimt.data,
                                  backgroundColor: createBarGradient(ctx, chartColors.bimt, isDark),
                                  borderColor: isDark ? chartColors.bimt.dark : chartColors.bimt.light,
                                  borderWidth: 1,
                                  hoverBackgroundColor: isDark ? chartColors.bimt.dark : chartColors.bimt.light,
                                  hoverBorderColor: isDark ? '#ffffff' : '#000000',
                                  barPercentage: 0.7,
                                  categoryPercentage: 0.8
                                }
                              ]
                            };
                          }}
                          options={options}
                        />
                      </div>
                    </HomeChartPanel>

                    <HomeChartPanel
                      title="EEMT"
                      badge={`${equipmentMetrics.eemt.n} equipamentos`}
                      accent={chartColors.eemt.light}
                      action={
                        <Button
                          className="px-3 btn-theme-outline"
                          onClick={() => { handlePlanilha("EEMT") }}
                          disabled={user?.permission?.home?.write !== true}
                          variant="outline-success"
                          size="sm"
                        >
                          <MdAssessment className="me-1" /> Relatório
                        </Button>
                      }
                    >
                      <div style={{ height: '350px', position: 'relative' }}>
                        <HorizontalBar
                          data={(canvas) => {
                            const ctx = canvas.getContext('2d');
                            const isDark = darkMode;

                            return {
                              labels: equipmentMetrics.eemt.labels,
                              datasets: [
                                {
                                  label: 'Equipamentos',
                                  data: equipmentMetrics.eemt.data,
                                  backgroundColor: createBarGradient(ctx, chartColors.eemt, isDark),
                                  borderColor: isDark ? chartColors.eemt.dark : chartColors.eemt.light,
                                  borderWidth: 1,
                                  hoverBackgroundColor: isDark ? chartColors.eemt.dark : chartColors.eemt.light,
                                  hoverBorderColor: isDark ? '#ffffff' : '#000000',
                                  barPercentage: 0.7,
                                  categoryPercentage: 0.8
                                }
                              ]
                            };
                          }}
                          options={options}
                        />
                      </div>
                    </HomeChartPanel>
                  </Col>
                </Row>

                <Row className="g-4">
                  <Col xs={12} lg={6}>
                    <HomeChartPanel
                      title="ESMR"
                      badge={`${equipmentMetrics.esmr.n} equipamentos`}
                      accent={chartColors.esmr.light}
                      action={
                        <Button
                          className="px-3 btn-theme-outline"
                          onClick={() => { handlePlanilha("ESMR") }}
                          disabled={user?.permission?.home?.write !== true}
                          variant="outline-success"
                          size="sm"
                        >
                          <MdAssessment className="me-1" /> Relatório
                        </Button>
                      }
                    >
                      <div style={{ height: '350px', position: 'relative' }}>
                        <HorizontalBar
                          data={(canvas) => {
                            const ctx = canvas.getContext('2d');
                            const isDark = darkMode;

                            return {
                              labels: equipmentMetrics.esmr.labels,
                              datasets: [
                                {
                                  label: 'Equipamentos',
                                  data: equipmentMetrics.esmr.data,
                                  backgroundColor: createBarGradient(ctx, chartColors.esmr, isDark),
                                  borderColor: isDark ? chartColors.esmr.dark : chartColors.esmr.light,
                                  borderWidth: 1,
                                  hoverBackgroundColor: isDark ? chartColors.esmr.dark : chartColors.esmr.light,
                                  hoverBorderColor: isDark ? '#ffffff' : '#000000',
                                  barPercentage: 0.7,
                                  categoryPercentage: 0.8
                                }
                              ]
                            };
                          }}
                          options={options}
                        />
                      </div>
                    </HomeChartPanel>
                  </Col>
                </Row>

                <div className="d-flex mt-4 gap-2">
                  <Button
                    variant="outline-success"
                    className="d-flex align-items-center btn-theme-outline"
                    href="/equipments"
                  >
                    <MdStorage className="me-1" /> Visualizar Equipamentos
                  </Button>

                  <Button
                    variant="outline-success"
                    className="d-flex align-items-center btn-theme-outline"
                    onClick={() => { window.open(process.env.REACT_APP_API_GLOBAL_SERVER + "/log", "_blank") }}
                  >
                    <MdOpenInNew className="me-1" /> Download Relatório
                  </Button>
                </div>
            </div>

            <div style={{ ...dashboardStyles.panel, padding: '20px' }}>
                <h4 className="mb-4 fw-bold section-header" style={dashboardStyles.titleText}>Certificados</h4>
                <CertificadoDash />
            </div>
            </div>
          </Container>
        </Page>
      ) : null}
    </>
  )
}

export default Home;
